/**
 * Drives one NS Business Assessment.
 *
 *   open link -> microphone permission ->
 *   POST /api/business-assessment/{token}/start -> join LiveKit room ->
 *   converse -> the assessment closes itself -> completion
 *
 * The client holds one credential, the access token from their link, and this
 * hook never learns anything else: no session id, no room name, no internal
 * stage. Completion is not something the browser decides — it polls the
 * backend, which reports it only when the assessment has actually closed.
 *
 * Audio only throughout: no video track is ever requested, published, or
 * subscribed to.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectionState, RoomEvent, Room, Track } from 'livekit-client';
import {
  fetchAssessment,
  fetchAssessmentStatus,
  fetchAssessmentTranscript,
  finishAssessment,
  leaveAssessment,
  startAssessment,
} from '../api/client';
import { historyCaptions, mergeCaptions } from './captions';
import { requestMicrophoneAccess } from '../lib/microphone';
import {
  AGENT_JOIN_TIMEOUT_MS,
  API_URL,
  ASSESSMENT_POLL_INTERVAL_MS,
  MAX_CAPTIONS,
} from '../config';

/** Phases of the client's page, in the order they normally occur. */
export const PHASES = {
  LOADING: 'loading',
  READY: 'ready',
  SUBMITTING: 'submitting',
  REQUESTING_MIC: 'requesting-mic',
  CONNECTING: 'connecting',
  LIVE: 'live',
  FINISHING: 'finishing',
  COMPLETE: 'complete',
  LEFT: 'left',
  INVALID: 'invalid',
  ERROR: 'error',
};

/** Data-channel topic the agent listens on for client control messages. */
const CONTROL_TOPIC = 'ns.control';

/**
 * How long to keep the room open after the assessment closes.
 *
 * NS Assistant says goodbye once the conversation is complete, and hanging up
 * on it would cut the last thing the client hears. The room is held until it
 * stops speaking, and no longer than this.
 */
const FAREWELL_TIMEOUT_MS = 30000;

export default function useBusinessAssessment(token) {
  const [phase, setPhase] = useState(PHASES.LOADING);
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState(null);
  const [captions, setCaptions] = useState([]);
  const [agentState, setAgentState] = useState(null);
  const [agentPresent, setAgentPresent] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const roomRef = useRef(null);
  // One start at a time. A double-click on the form must not open two rooms.
  const startingRef = useRef(false);
  // One finish per conversation. Ending is something the client chooses, so a
  // second click — or an End that lands while the first is still in flight —
  // must not send the signal twice.
  const finishingRef = useRef(false);
  const audioElementsRef = useRef(new Map());
  const joinedRef = useRef(false);
  const agentStateRef = useRef(null);
  const isPausedRef = useRef(false);

  agentStateRef.current = agentState;
  isPausedRef.current = isPaused;

  /**
   * Merge incoming transcription segments. See `captions.js` for the rules.
   */
  const mergeSegments = useCallback((segments, role) => {
    setCaptions((previous) => mergeCaptions(previous, segments, role, MAX_CAPTIONS));
  }, []);

  /** Detach audio elements and leave the room. */
  const teardownRoom = useCallback(async () => {
    const room = roomRef.current;
    roomRef.current = null;
    audioElementsRef.current.forEach((element) => element.remove());
    audioElementsRef.current.clear();
    if (room) {
      room.removeAllListeners();
      await room.disconnect().catch(() => {});
    }
  }, []);

  // What this link is for, and whether it has already been used up.
  //
  // An assessment that was started earlier also brings its conversation back.
  // The page keeps nothing of its own between visits — the client may return
  // from another browser or another machine — so what was said is read from
  // the backend, which is the only thing that actually remembers it.
  useEffect(() => {
    let cancelled = false;
    fetchAssessment(token).then(
      (body) => {
        if (cancelled) return;
        setAssessment(body);
        setPhase(body.complete ? PHASES.COMPLETE : PHASES.READY);
        // Only a link that continues something has anything to show. A new
        // one has no conversation, and a finished one goes to the completion
        // page, which is not the conversation panel.
        if (!body.resumable) return;
        fetchAssessmentTranscript(token).then(
          (history) => {
            // Seeded only if nothing has been said since: a live segment that
            // arrived while this was in flight is newer than the file it
            // would be replaced by.
            if (cancelled) return;
            setCaptions((previous) =>
              previous.length ? previous : historyCaptions(history?.turns),
            );
          },
          () => { /* the conversation still works without its history */ },
        );
      },
      (apiError) => {
        if (cancelled) return;
        setPhase(apiError.status === 404 ? PHASES.INVALID : PHASES.ERROR);
        setError(
          apiError.status === 404
            ? 'This link is not valid. Please check the link you were sent.'
            : 'The service could not be reached. Please try again shortly.',
        );
      },
    );
    return () => { cancelled = true; };
  }, [token]);

  const start = useCallback(async (identity) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setError(null);
    // The conversation so far is deliberately NOT cleared. Connecting is not
    // the start of the conversation — on a resumed link it is the start of
    // another sitting of the same one, and the client should see what they
    // are continuing rather than a panel that looks like nothing was kept.
    // New turns append after it; `captions.js` keeps live segments from
    // merging into a stored one.
    setAgentState(null);
    setAgentPresent(false);

    // 1. The backend first, and nothing else until it has accepted. Until the
    //    identity is stored there is no room token, so there is nothing to
    //    connect to — and asking for the microphone ahead of that would leave
    //    a client who mistyped their organisation holding a permission prompt
    //    for a conversation that never started. This phase is not one of the
    //    conversation phases, so the form stays on screen while it runs.
    setPhase(PHASES.SUBMITTING);
    let connection;
    try {
      connection = await startAssessment(token, identity);
    } catch (apiError) {
      startingRef.current = false;
      if (apiError.status === 409) {
        setPhase(PHASES.COMPLETE);
        return;
      }
      setError(
        apiError.status === 404
          ? 'This link is not valid. Please check the link you were sent.'
          : apiError.message,
      );
      // Back to the form, with what they typed still in it, so they can fix
      // it and try again. Nothing was started, so there is nothing to undo.
      setPhase(apiError.status === 404 ? PHASES.INVALID : PHASES.READY);
      return;
    }

    // 2. Accepted. Only now is this a conversation: the microphone is asked
    //    for, and the avatar appears with it.
    setPhase(PHASES.REQUESTING_MIC);
    try {
      await requestMicrophoneAccess();
    } catch (micError) {
      setError(micError.message);
      setPhase(PHASES.READY);
      startingRef.current = false;
      return;
    }
    startingRef.current = false;
    setPhase(PHASES.CONNECTING);

    // 3. Join the room. Audio only — no video capture or playback.
    const room = new Room({ adaptiveStream: false, dynacast: false });
    roomRef.current = room;

    room
      .on(RoomEvent.TranscriptionReceived, (segments, participant) => {
        const isLocal = participant?.identity === room.localParticipant?.identity;
        mergeSegments(segments, isLocal ? 'user' : 'agent');
      })
      .on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== Track.Kind.Audio) return;
        const element = track.attach();
        element.style.display = 'none';
        if (isPausedRef.current) {
          element.muted = true;
        }
        document.body.appendChild(element);
        audioElementsRef.current.set(track.sid, element);
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        const element = audioElementsRef.current.get(track.sid);
        if (element) {
          track.detach(element);
          element.remove();
          audioElementsRef.current.delete(track.sid);
        }
      })
      .on(RoomEvent.ParticipantConnected, () => {
        setAgentPresent(true);
        setError(null);
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        const stillThere = room.numParticipants > 0;
        setAgentPresent(stillThere);
        if (!stillThere) {
          setError('NS Assistant disconnected. Everything said so far has been saved.');
        }
      })
      .on(RoomEvent.ParticipantAttributesChanged, (changed) => {
        if ('lk.agent.state' in changed) setAgentState(changed['lk.agent.state']);
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        if (state === ConnectionState.Reconnecting) setError('Connection lost — reconnecting…');
        else if (state === ConnectionState.Connected) setError(null);
      })
      .on(RoomEvent.MediaDevicesError, (deviceError) => {
        setError(`Microphone error: ${deviceError.message}`);
      });

    try {
      await room.connect(connection.livekit_url, connection.token);
      await room.localParticipant.setMicrophoneEnabled(true);
      joinedRef.current = true;
      setMicEnabled(true);
      setIsPaused(false);
      setAgentPresent(room.numParticipants > 0);
      setPhase(PHASES.LIVE);
    } catch (connectError) {
      await teardownRoom();
      setError(`Could not join the conversation: ${connectError.message}`);
      setPhase(PHASES.READY);
    }
  }, [mergeSegments, teardownRoom, token]);

  /**
   * Leave the room and tell the backend which of the two things happened.
   *
   *   explicit   the client pressed End Conversation -> `/finish`
   *   ordinary   the conversation closed itself, or the page went away
   *              -> `/leave`, which keeps the link resumable
   *
   * The difference is everything. `/finish` ENDS the assessment and spends
   * the link; `/leave` keeps both alive. Which is why a page being torn down
   * must never send the first one — an unload beacon carries no body and can
   * express no intent, and a closed tab is not a decision to stop.
   */
  const leave = useCallback(
    async (nextPhase, { explicit = false } = {}) => {
      if (explicit && finishingRef.current) return;
      if (explicit) finishingRef.current = true;

      // Cleared before the request goes out, so the unload beacon cannot fire
      // a second, contradicting signal while this one is in flight.
      const wasJoined = joinedRef.current;
      joinedRef.current = false;
      await teardownRoom();

      if (wasJoined) {
        // A failed signal must not strand the client on a live-looking page.
        // The room is already gone, so the page moves on either way, and the
        // backend still has the session exactly as it was left.
        await (explicit ? finishAssessment(token) : leaveAssessment(token)).catch(() => {});
      }

      setAgentState(null);
      setAgentPresent(false);
      setIsPaused(false);
      setPhase(nextPhase);
    },
    [teardownRoom, token],
  );

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !micEnabled;
    if (!isPausedRef.current) {
      await room.localParticipant.setMicrophoneEnabled(next);
    }
    setMicEnabled(next);
  }, [micEnabled]);

  /** Toggle pausing audio playback and microphone input. */
  const togglePausePlay = useCallback(async () => {
    const room = roomRef.current;
    const next = !isPaused;
    setIsPaused(next);

    if (room && room.localParticipant) {
      if (next) {
        // Pause: disable local mic publishing and mute all AI audio elements
        await room.localParticipant.setMicrophoneEnabled(false);
        audioElementsRef.current.forEach((element) => {
          element.muted = true;
        });
        // Interrupt AI if currently speaking
        if (agentStateRef.current === 'speaking') {
          const payload = new TextEncoder().encode(JSON.stringify({ action: 'interrupt' }));
          await room.localParticipant
            .publishData(payload, { reliable: true, topic: CONTROL_TOPIC })
            .catch(() => {});
        }
      } else {
        // Resume: restore mic if enabled, unmute AI audio elements
        if (micEnabled) {
          await room.localParticipant.setMicrophoneEnabled(true);
        }
        audioElementsRef.current.forEach((element) => {
          element.muted = false;
        });
      }
    }
  }, [isPaused, micEnabled]);

  /** Send speech turn immediately without waiting for VAD silence timeout. */
  const sendAudioTurn = useCallback(async () => {
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected || isPausedRef.current) return;
    setAgentState('thinking');
    const payload = new TextEncoder().encode(JSON.stringify({ action: 'send' }));
    await room.localParticipant
      .publishData(payload, { reliable: true, topic: CONTROL_TOPIC })
      .catch(() => {});
  }, []);

  // Re-enable Send button when response arrives/agent speaks/listens or on error
  useEffect(() => {
    if (agentState === 'speaking' || agentState === 'listening' || !agentState) {
      setIsProcessing(false);
    }
  }, [agentState]);

  useEffect(() => {
    if (error) {
      setIsProcessing(false);
    }
  }, [error]);

  /** Ask NS Assistant to stop talking. Speaking over it does the same thing. */
  const interrupt = useCallback(async () => {
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    setAgentState('listening');
    const payload = new TextEncoder().encode(JSON.stringify({ action: 'interrupt' }));
    await room.localParticipant
      .publishData(payload, { reliable: true, topic: CONTROL_TOPIC })
      .catch(() => {});
  }, []);

  // Leave the room cleanly if the component unmounts.
  useEffect(() => () => { teardownRoom(); }, [teardownRoom]);

  // If NS Assistant never arrives, say so rather than leaving the client in
  // silence — the one failure a caller cannot diagnose for themselves.
  useEffect(() => {
    if (phase !== PHASES.LIVE || agentPresent) return undefined;
    const timer = setTimeout(() => {
      setError(
        'NS Assistant has not joined yet. Please wait a moment, or reload this page to try again.',
      );
    }, AGENT_JOIN_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [phase, agentPresent]);

  // Watch for completion. The backend reports it only once the assessment has
  // genuinely closed, which happens after the client has confirmed the problem
  // themselves — so this cannot fire early, and nothing the browser does can
  // make it fire at all.
  useEffect(() => {
    if (phase !== PHASES.LIVE) return undefined;

    let cancelled = false;
    const check = () => {
      fetchAssessmentStatus(token)
        .then((status) => {
          if (!cancelled && status.complete) setPhase(PHASES.FINISHING);
        })
        .catch(() => { /* a failed poll must never disturb the conversation */ });
    };

    check();
    const timer = setInterval(check, ASSESSMENT_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [phase, token]);

  // The assessment has closed: let NS Assistant finish saying goodbye, then
  // hang up.
  useEffect(() => {
    if (phase !== PHASES.FINISHING) return undefined;

    const deadline = Date.now() + FAREWELL_TIMEOUT_MS;
    const timer = setInterval(() => {
      const stillTalking = agentStateRef.current === 'speaking';
      if (!stillTalking || Date.now() > deadline) {
        clearInterval(timer);
        leave(PHASES.COMPLETE);
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [phase, leave]);

  // A refresh or tab close tells the backend the client left the room.
  // `sendBeacon` survives page teardown, where a normal fetch is cancelled.
  // It does NOT end the assessment: the backend marks a session incomplete
  // unless the conversation actually closed, so coming back resumes it.
  useEffect(() => {
    const handleUnload = () => {
      if (!joinedRef.current) return;
      navigator.sendBeacon?.(`${API_URL}/api/business-assessment/${token}/leave`, new Blob([], {
        type: 'application/json',
      }));
    };
    window.addEventListener('pagehide', handleUnload);
    return () => window.removeEventListener('pagehide', handleUnload);
  }, [token]);

  return {
    phase,
    assessment,
    error,
    captions,
    agentState,
    agentPresent,
    micEnabled,
    isPaused,
    isProcessing,
    recording: phase === PHASES.LIVE && agentPresent && !isPaused,
    start,
    // The End control, and the only thing that sends the explicit signal.
    // Choosing to finish IS finishing: the backend completes the assessment
    // on this call, whatever the conversation covered, and the link will not
    // reopen. So the page shows the completion state — the "come back any
    // time" panel belongs to a leave, which is a different thing.
    finish: () => leave(PHASES.COMPLETE, { explicit: true }),
    toggleMic,
    togglePausePlay,
    sendAudioTurn,
    interrupt,
  };
}
